/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useRecordingData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading recording data and AI profiles.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { useContextStore } from '../../../store/context';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { getRecordingById } from '../api/recordingManagementApi';
import { VoiceRecording } from '../types';
import type { AiProfile } from '../../ai-profiles/api/aiProfilesApi';

/**
 * Custom React hook for fetching a single Recording's data and associated AI TTS profiles.
 * Automatically manages the loading, error, and playback audio states.
 * 
 * @param id - The numeric ID or UUID of the recording to load.
 * @returns Object containing the recording data, AI profiles, loading state, and audio URLs.
 */
export function useRecordingData(id: string | undefined) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedTenant } = useContextStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [recording, setRecording] = useState<VoiceRecording | null>(null);
  const [aiProfiles, setAiProfiles] = useState<AiProfile[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Load AI profiles
  const loadAiProfiles = useCallback(async (): Promise<AiProfile[]> => {
    const tenantSlug = selectedTenant?.slug || 'main';
    const response = await fetch(buildApiUrl('/api/ai/profiles?type=tts&status=true'), {
      method: 'GET',
      headers: getApiHeaders(tenantSlug),
    });

    if (!response.ok) {
      return [];
    }

    const result = await response.json();
    const profiles = result.data || [];
    setAiProfiles(profiles);
    return profiles;
  }, [selectedTenant]);

  // Load recording data
  const loadRecording = useCallback(
    async (recordingId: number | string, profiles: AiProfile[]) => {
      try {
        setIsLoading(true);
        const recordingData = await getRecordingById(recordingId);
        setRecording(recordingData);

        // Auto-load audio for existing recording
        try {
          const audioBlob = await fetch(buildApiUrl(`/api/telephony/recordings/${recordingId}/play`), {
            method: 'GET',
            headers: getApiHeaders(),
          }).then((res) => res.blob());
          const audioObjectUrl = URL.createObjectURL(audioBlob);
          setAudioUrl(audioObjectUrl);
        } catch (audioError) {
          // Don't show error for auto-load, user can still use Play button
        }
      } catch (error) {
        showError(error.message || t('recordingManagement.errors.loadFailed'));
        navigate('/settings/voice-prompts/recording');
      } finally {
        setIsLoading(false);
      }
    },
    [showError, t, navigate],
  );

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      const profiles = await loadAiProfiles();

      if (id && id !== 'new') {
        setIsEditMode(true);
        await loadRecording(id, profiles);
      } else {
        // Reset state when navigating to 'new'
        setIsEditMode(false);
        setRecording(null);
        // Clean up audio URL
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
          setAudioUrl(null);
        }
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  return {
    isLoading,
    isEditMode,
    recording,
    aiProfiles,
    audioUrl,
    setAudioUrl,
    loadAiProfiles,
    loadRecording,
  };
}

